import { getSubjectLabels } from '@/lib/settings';
import React, { useEffect, useState } from 'react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../../../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Textarea } from '../../../components/ui/textarea';
import { RichTextEditor } from '../../../components/ui/rich-text-editor';
import { MediaPicker } from '../../../components/cms/MediaPicker';
import { useStandort } from '../../../components/layouts/use-standort';
import { api } from '../../../lib/api';
import LoeschenMitGrundDialog from '../../../components/ui/loeschen-mit-grund-dialog';
import { toast } from 'sonner';
import { PlusCircle, RefreshCw, GripVertical, Image as ImageIcon, X } from 'lucide-react';

interface Fach {
  id: number;
  name: string;
}

type PortalScope = 'intern' | 'extern' | 'beide';

type PortalItem = {
  id: number;
  standortId?: number | null;
  standortIds: number[];
  type: 'news' | 'link';
  scope?: PortalScope;
  title: string;
  teaser?: string | null;
  content?: string | null;
  tag?: string | null;
  targetFachIds?: number[];
  url?: string | null;
  imageUrl?: string | null;
  sortOrder: number;
  active: boolean;
  startsAt?: string | null;
  endsAt?: string | null;
  createdAt?: string;
  updatedAt?: string;
};

const SCOPE_LABELS: Record<PortalScope, string> = {
  intern: 'Kundenportal (intern)',
  extern: 'Pinnwand (öffentlich)',
  beide: 'Beide',
};

const toInputDateTime = (value?: string | null) => {
  if (!value) return '';
  return value.replace(' ', 'T').slice(0, 16);
};

const toDbDateTime = (value: string) => {
  if (!value) return null;
  const normalized = value.includes('T') ? value.replace('T', ' ') : value;
  return normalized.length === 16 ? `${normalized}:00` : normalized;
};

const CustomerPortalNews = () => {
  // Der aktive Standort kommt aus dem globalen Standort-Wechsler (Kopfzeile),
  // daher keine zweite Standort-Auswahl mehr in dieser Ansicht.
  const { selectedStandortId, selectedStandort, standorte } = useStandort();
  const [items, setItems] = useState<PortalItem[]>([]);
  const [faecher, setFaecher] = useState<Fach[]>([]);
  const [loading, setLoading] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [imagePickerOpen, setImagePickerOpen] = useState(false);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [selected, setSelected] = useState<PortalItem | null>(null);
  const [zuLoeschen, setZuLoeschen] = useState<PortalItem | null>(null);
  const [form, setForm] = useState<Partial<PortalItem>>({
    type: 'news',
    sortOrder: 0,
    active: true,
  });

  const loadItems = async (sid?: number | null) => {
    if (!sid) return;
    setLoading(true);
    try {
      const data = await api.get<PortalItem[]>(`/api/portal-items?standort_id=${sid}`);
      setItems(data);
    } catch (e) {
      console.error(e);
      toast.error('Einträge konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  const loadFaecher = async () => {
    try {
      const data = await api.get<Fach[]>('/api/faecher?active_only=false');
      setFaecher(Array.isArray(data) ? data.map((f) => ({ id: f.id, name: f.name })) : []);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    loadFaecher();
  }, []);

  useEffect(() => {
    loadItems(selectedStandortId);
  }, [selectedStandortId]);

  const openCreate = () => {
    if (!selectedStandortId) {
      toast.error('Bitte zuerst oben einen Standort auswählen');
      return;
    }
    setSelected(null);
    setForm({
      standortIds: selectedStandortId ? [selectedStandortId] : [],
      type: 'news',
      scope: 'intern',
      title: '',
      teaser: '',
      content: '',
      tag: '',
      targetFachIds: [],
      url: '',
      imageUrl: '',
      sortOrder: 0,
      active: true,
      startsAt: null,
      endsAt: null,
    });
    setIsEditOpen(true);
  };

  const openEdit = (item: PortalItem) => {
    setSelected(item);
    setForm({ ...item });
    setIsEditOpen(true);
  };

  const handleSave = async () => {
    if (!form.standortIds || form.standortIds.length === 0) {
      toast.error('Mindestens ein Standort ist erforderlich');
      return;
    }
    if (!form.title || form.title.trim() === '') {
      toast.error('Titel ist erforderlich');
      return;
    }
    const payload = {
      standortIds: form.standortIds,
      type: form.type || 'news',
      scope: form.scope || 'intern',
      title: form.title?.trim(),
      teaser: form.teaser || null,
      content: form.content || null,
      tag: form.tag || null,
      targetFachIds: form.targetFachIds || [],
      url: form.url || null,
      imageUrl: form.imageUrl || null,
      // Reihenfolge wird per Drag & Drop in der Liste gepflegt; neue Einträge ans Ende.
      sortOrder: selected ? Number(form.sortOrder ?? 0) : items.length,
      active: !!form.active,
      startsAt: toDbDateTime(form.startsAt || ''),
      endsAt: toDbDateTime(form.endsAt || ''),
    };
    try {
      if (selected) {
        const updated = await api.put<PortalItem>(`/api/portal-items/${selected.id}`, payload);
        setItems(prev => prev.map(it => (it.id === updated.id ? updated : it)));
        toast.success('Eintrag aktualisiert');
      } else {
        const created = await api.post<PortalItem>('/api/portal-items', payload);
        setItems(prev => [...prev, created]);
        toast.success('Eintrag erstellt');
      }
      setIsEditOpen(false);
    } catch (e) {
      console.error(e);
      toast.error('Speichern fehlgeschlagen');
    }
  };

  const toggleStandort = (standortId: number) => {
    setForm((prev) => {
      const cur = prev.standortIds || [];
      return {
        ...prev,
        standortIds: cur.includes(standortId)
          ? cur.filter((id) => id !== standortId)
          : [...cur, standortId],
      };
    });
  };

  const toggleTargetFach = (fachId: number) => {
    setForm(prev => {
      const cur = prev.targetFachIds || [];
      return {
        ...prev,
        targetFachIds: cur.includes(fachId) ? cur.filter(id => id !== fachId) : [...cur, fachId],
      };
    });
  };

  // News brauchen keinen Grund zum Löschen (Chris, 30.09.2026) — nur die Rückfrage.
  // Das Backend nimmt die Löschung dafür ohne Grund an (grund_optional, T-0143).
  const handleDelete = async (id: number) => {
    await api.delete(`/api/portal-items/${id}`);
    setItems(prev => prev.filter(it => it.id !== id));
    setZuLoeschen(null);
    toast.success('Eintrag gelöscht');
  };

  const persistOrder = async (ordered: PortalItem[]) => {
    try {
      await api.post('/api/portal-items/reorder', { orderedIds: ordered.map(i => i.id) });
    } catch (e) {
      console.error(e);
      toast.error('Reihenfolge konnte nicht gespeichert werden');
      loadItems(selectedStandortId);
    }
  };

  const handleDrop = (targetIndex: number) => {
    if (dragIndex === null || dragIndex === targetIndex) {
      setDragIndex(null);
      return;
    }
    const next = [...items];
    const [moved] = next.splice(dragIndex, 1);
    next.splice(targetIndex, 0, moved);
    setItems(next);
    setDragIndex(null);
    void persistOrder(next);
  };

  let listContent: React.ReactNode = null;
  if (loading) {
    listContent = (
      <div className="rounded-lg border bg-card p-8 text-center text-muted-foreground">Lade Einträge…</div>
    );
  } else if (items.length === 0) {
    listContent = (
      <div className="rounded-lg border bg-card p-8 text-center text-muted-foreground">Noch keine Einträge vorhanden.</div>
    );
  } else {
    listContent = (
      <div className="flex flex-col gap-3">
        <p className="text-xs text-muted-foreground">Reihenfolge per Ziehen am Griff ändern.</p>
        {items.map((item, index) => (
          <Card
            key={item.id}
            onDragOver={e => e.preventDefault()}
            onDrop={() => handleDrop(index)}
            className={dragIndex === index ? 'opacity-60' : undefined}
          >
            <div className="flex items-start gap-2 p-1">
              <button
                type="button"
                draggable
                onDragStart={() => setDragIndex(index)}
                onDragEnd={() => setDragIndex(null)}
                className="mt-4 ml-2 cursor-grab text-muted-foreground hover:text-foreground active:cursor-grabbing"
                aria-label="Zum Sortieren ziehen"
                title="Zum Sortieren ziehen"
              >
                <GripVertical className="h-5 w-5" />
              </button>
              <div className="min-w-0 flex-1">
                <CardHeader>
                  <CardTitle className="text-lg">{item.title}</CardTitle>
                  <CardDescription>
                    {item.type === 'news' ? 'Neuigkeit' : 'Link'} · {SCOPE_LABELS[item.scope || 'intern']} · {item.active ? 'Aktiv' : 'Inaktiv'}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-2 text-sm text-muted-foreground">
                  <p>
                    Standorte:{' '}
                    {(item.standortIds && item.standortIds.length > 0
                      ? item.standortIds
                      : item.standortId
                        ? [item.standortId]
                        : []
                    )
                      .map((id) => standorte.find((s) => s.id === id)?.name || `#${id}`)
                      .join(', ') || '—'}
                  </p>
                  {item.tag && <p>Kategorie: {item.tag}</p>}
                  {item.targetFachIds && item.targetFachIds.length > 0 && (
                    <p>Zielgruppe: {item.targetFachIds.map(id => faecher.find(f => f.id === id)?.name || `#${id}`).join(', ')}</p>
                  )}
                  {item.teaser && <p>{item.teaser}</p>}
                  {item.imageUrl && (
                    <img src={item.imageUrl} alt="" className="h-16 w-auto rounded border object-cover" />
                  )}
                  {(item.startsAt || item.endsAt) && (
                    <p>Zeitraum: {item.startsAt || 'sofort'} – {item.endsAt || 'unbegrenzt'}</p>
                  )}
                </CardContent>
                <CardFooter className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => openEdit(item)}>Bearbeiten</Button>
                  <Button variant="destructive" onClick={() => setZuLoeschen(item)}>Löschen</Button>
                </CardFooter>
              </div>
            </div>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold leading-7">News (Pinnwand & Kundenportal)</h1>
        <p className="text-muted-foreground">
          Neuigkeiten und Links pro Standort. Über die Sichtbarkeit steuerst du, ob ein Eintrag
          intern im Kundenportal (nach Login), extern auf der öffentlichen Standort-Pinnwand oder in beiden erscheint.
        </p>
      </div>

      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={() => loadItems(selectedStandortId)}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Aktualisieren
          </Button>
          {selectedStandort && (
            <span className="text-sm text-muted-foreground">
              Standort: <span className="font-medium text-foreground">{selectedStandort.name}</span>
            </span>
          )}
        </div>
        <Button onClick={openCreate} disabled={!selectedStandortId}>
          <PlusCircle className="mr-2 h-4 w-4" />
          Neuer Eintrag
        </Button>
      </div>

      {!selectedStandortId ? (
        <div className="rounded-lg border bg-card p-8 text-center text-sm text-muted-foreground">
          Bitte oben einen Standort auswählen, um dessen Einträge zu pflegen.
        </div>
      ) : (
        listContent
      )}

      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selected ? 'Eintrag bearbeiten' : 'Neuen Eintrag erstellen'}</DialogTitle>
            <DialogDescription>Sichtbarkeit steuert, ob der Eintrag intern (Kundenportal), extern (öffentliche Pinnwand) oder in beiden erscheint.</DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4 sm:grid-cols-2">
            <div className="grid gap-2 sm:col-span-2">
              <Label>Standorte</Label>
              <p className="text-xs text-muted-foreground">
                Ein Eintrag kann für mehrere Standorte gelten (z. B. allgemeine Infos wie Schulferien).
              </p>
              <div className="flex flex-wrap gap-2">
                {standorte.map((s) => {
                  const active = (form.standortIds || []).includes(s.id);
                  return (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => toggleStandort(s.id)}
                      className={`rounded-full border px-3 py-1 text-sm transition-colors ${
                        active
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-input text-muted-foreground hover:bg-muted'
                      }`}
                    >
                      {s.name}
                    </button>
                  );
                })}
                {standorte.length === 0 && (
                  <span className="text-sm text-muted-foreground">Keine Standorte verfügbar.</span>
                )}
              </div>
            </div>

            <div className="grid gap-2">
              <Label>Typ</Label>
              <Select value={form.type || 'news'} onValueChange={v => setForm(prev => ({ ...prev, type: v as 'news' | 'link' }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Typ auswählen" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="news">Neuigkeit</SelectItem>
                  <SelectItem value="link">Link</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-2">
              <Label>Sichtbarkeit</Label>
              <Select value={form.scope || 'intern'} onValueChange={v => setForm(prev => ({ ...prev, scope: v as PortalScope }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Sichtbarkeit auswählen" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="intern">Kundenportal (intern, nach Login)</SelectItem>
                  <SelectItem value="extern">Pinnwand (öffentliche Standort-Seite)</SelectItem>
                  <SelectItem value="beide">Beide</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-2">
              <Label>Titel</Label>
              <Input value={form.title || ''} onChange={e => setForm(prev => ({ ...prev, title: e.target.value }))} />
            </div>

            <div className="grid gap-2">
              <Label>Kategorie (optional)</Label>
              <Input value={form.tag || ''} onChange={e => setForm(prev => ({ ...prev, tag: e.target.value }))} placeholder="z. B. Veranstaltung, Neu" />
            </div>

            <div className="grid gap-2 sm:col-span-2">
              <Label>Teaser (Kurztext)</Label>
              <Textarea value={form.teaser || ''} onChange={e => setForm(prev => ({ ...prev, teaser: e.target.value }))} />
            </div>

            <div className="grid gap-2 sm:col-span-2">
              <Label>Inhalt (Detailtext)</Label>
              <RichTextEditor value={form.content || ''} onChange={html => setForm(prev => ({ ...prev, content: html }))} />
            </div>

            <div className="grid gap-2 sm:col-span-2">
              <Label>Zielgruppe: {getSubjectLabels().plural} (optional)</Label>
              <p className="text-xs text-muted-foreground">
                Nur für interne Kundenportal-News wirksam. Leer = alle Kunden. Sonst nur Kunden,
                die eines der gewählten {getSubjectLabels().plural} belegen.
              </p>
              <div className="flex flex-wrap gap-2">
                {faecher.map(f => {
                  const selected = (form.targetFachIds || []).includes(f.id);
                  return (
                    <button
                      key={f.id}
                      type="button"
                      onClick={() => toggleTargetFach(f.id)}
                      className={`rounded-full border px-3 py-1 text-sm transition-colors ${
                        selected
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-input text-muted-foreground hover:bg-muted'
                      }`}
                    >
                      {f.name}
                    </button>
                  );
                })}
                {faecher.length === 0 && (
                  <span className="text-sm text-muted-foreground">Keine {getSubjectLabels().plural} verfügbar.</span>
                )}
              </div>
            </div>

            <div className="grid gap-2">
              <Label>URL (optional)</Label>
              <Input value={form.url || ''} onChange={e => setForm(prev => ({ ...prev, url: e.target.value }))} />
            </div>

            <div className="grid gap-2 sm:col-span-2">
              <Label>Bild (optional)</Label>
              <div className="flex items-start gap-3">
                <div className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-md border bg-muted/30">
                  {form.imageUrl ? (
                    <img src={form.imageUrl} alt="" className="h-full w-full object-cover" />
                  ) : (
                    <ImageIcon className="h-6 w-6 text-muted-foreground" />
                  )}
                </div>
                <div className="flex flex-1 flex-col gap-2">
                  <Input
                    placeholder="Bild-URL"
                    value={form.imageUrl || ''}
                    onChange={e => setForm(prev => ({ ...prev, imageUrl: e.target.value }))}
                  />
                  <div className="flex gap-2">
                    <Button type="button" variant="outline" size="sm" onClick={() => setImagePickerOpen(true)}>
                      <ImageIcon className="mr-2 h-4 w-4" /> Aus Mediathek
                    </Button>
                    {form.imageUrl && (
                      <Button type="button" variant="ghost" size="sm" onClick={() => setForm(prev => ({ ...prev, imageUrl: '' }))}>
                        <X className="mr-1 h-4 w-4" /> Entfernen
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="grid gap-2">
              <Label>Status</Label>
              <Select value={form.active ? '1' : '0'} onValueChange={v => setForm(prev => ({ ...prev, active: v === '1' }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Status auswählen" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Aktiv</SelectItem>
                  <SelectItem value="0">Inaktiv</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-2">
              <Label>Start (optional)</Label>
              <Input type="datetime-local" value={toInputDateTime(form.startsAt || null)} onChange={e => setForm(prev => ({ ...prev, startsAt: e.target.value }))} />
            </div>

            <div className="grid gap-2">
              <Label>Ende (optional)</Label>
              <Input type="datetime-local" value={toInputDateTime(form.endsAt || null)} onChange={e => setForm(prev => ({ ...prev, endsAt: e.target.value }))} />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditOpen(false)}>Abbrechen</Button>
            <Button onClick={handleSave}>{selected ? 'Speichern' : 'Erstellen'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <MediaPicker
        open={imagePickerOpen}
        onClose={() => setImagePickerOpen(false)}
        onSelect={url => setForm(prev => ({ ...prev, imageUrl: url }))}
      />

      <LoeschenMitGrundDialog
        mitGrund={false}
        offen={zuLoeschen !== null}
        titel={`„${zuLoeschen?.title || 'Eintrag'}“ löschen?`}
        onAbbrechen={() => setZuLoeschen(null)}
        onLoeschen={async () => {
          if (zuLoeschen) await handleDelete(zuLoeschen.id);
        }}
      />
    </div>
  );
};

export default CustomerPortalNews;
